import React, {useEffect, useState } from 'react';
import {Trans, useTranslation} from 'react-i18next';

import '../../Custom.css';
import {
    DefaultButton,
    DetailsListLayoutMode,
    FontWeights,
    IButtonStyles,
    IColumn,
    IComboBoxOption,
    IconButton,
    IContextualMenuProps,
    Modal,
    Nav,
    Panel,
    PanelType,
    SelectionMode,
    Separator,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react";
import {useBranding} from '../../hooks/useBranding';
import ApprovalService, {IGetApprovalStepRequest, IGetApprovalStepResponse} from "../../services/Approval";
import {contentStyles, iconButtonStyles, navStyles, RolesAdd} from "./";
import StatusMessage, {StatusEnum} from "../../share/StatusMessage";
import {decode} from 'html-entities'
import RolesService, {IDeleteRolesRequest, IGetRolesRequest, IGetRolesResponse} from "../../services/Roles";
import CustomDialog from "../../share/CustomDialog";

type Properties = {
    titleId: string,
    cancel: any
    getLinkGroup: any
    option: string
    setIsOpenMenu: any
    showModal: any
    onLinkClick: any
    templateId: string,
    isOpenMenu: any
}

export function Roles({
                          showModal,
                          option,
                          templateId,
                          getLinkGroup,
                          titleId,
                          cancel,
                          isOpenMenu,
                          onLinkClick,
                          setIsOpenMenu
                      }: Properties) {

    const {branding} = useBranding()
    const {t} = useTranslation(['roles', 'common'])
    const iconListButtonStyles: Partial<IButtonStyles> = {root: {float: 'left', height: 'inherit'}};


    const [roles, setRoles] = useState<IGetRolesResponse[]>([])
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessage, setStatusMessage] = useState<StatusEnum>(StatusEnum.Info)
    const [hasTimerMessage, setHasTimerMessage] = useState(false)
    const [message, setMessage] = useState("")
    const [isLoadingRoles, setIsLoadingRoles] = useState(true)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isLoadingSteps, setIsLoadingSteps] = useState(false)
    const [currentRole, setCurrentRole] = useState<IGetRolesResponse | null>(null)
    const [showDeleteRole, setShowDeleteRole] = useState(false)
    const [showAddRole, setShowAddRole] = useState(false)
    const [stepsCBOptions, setStepsCBOptions] = useState<IComboBoxOption[]>([])


    useEffect(() => {
        if (showModal) {
            loadRoles().then()
            loadApproveSteps().then()
        }
    }, [])


    /**
     * Asynchronously loads and processes approval steps for a given template.
     */
    const loadApproveSteps = async () => {

        setIsLoadingSteps(true)

        const request: IGetApprovalStepRequest = {
            TemplateId: templateId,
        }

        await ApprovalService.getApprovalStep(request)
            .then(data => {
                try {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalStepResponse>

                    if (Array.isArray(resultData)) {
                        resultData.sort((a, b) => a.Order - b.Order);
                        setStepsCBOptions(resultData.map(step => ({key: step.Id, text: step.Name})))
                    }
                } catch (_) {
                }

            })
            .catch((_) => {

            })
            .finally(() => {
                setIsLoadingSteps(false)
            })
    }


    /**
     * Displays an approval message with a specified status.
     *
     * @param {string} message - The message content to be displayed.
     * @param {StatusEnum} status - The status associated with the approval message.
     * @param hasTimer
     * @return {void} This function does not return a value.
     */

    function ShowMessageApprove(message: string, status: StatusEnum, hasTimer: boolean = false): void {
        setMessage(decode(message))
        setStatusMessage(status)
        setHasTimerMessage(hasTimer)
        setShowMessage(true)
    }


    /**
     * Generates a contextual menu for role actions.
     *
     * @returns {IContextualMenuProps} The properties for the contextual menu.
     * @param item
     **/

    const menuRolesProps = (item: IGetRolesResponse): IContextualMenuProps => {
        return {
            items: [

                {
                    key: 'delete',
                    text: t("Common.MenuAction.Delete", {ns: "common"}),
                    title: t("Common.MenuAction.Delete", {ns: "common"}),
                    iconProps: {iconName: 'Trash'},
                    onClick: () => {
                        setCurrentRole(item)
                        setShowDeleteRole(true)
                    }
                }
            ],
            directionalHintFixed: true
        }
    }


    /**
     * Handles the closing of the add role modal.
     * @param update
     * @return {void} This function does not return a value.
     */

    const onCloseAddModal = (update: boolean = false): void => {
        setShowAddRole(false)

        if (update) {
            ShowMessageApprove(t('message.successCreated', {ns: 'common'}), StatusEnum.Success, true)
            loadRoles().then()
        }
    }



    /**
     * Defines the columns for the roles list.
     * Each column has properties such as key, name, fieldName, width, and rendering functions.
     * The columns include role name, approval step ID, and action buttons.
     *
     * @type {IColumn[]}
     */

    const rolesColumns: IColumn[] = [

        {
            key: 'column1',
            name: t('columnGroupName', {ns: 'roles'}),
            fieldName: 'RoleName',
            minWidth: 200,
            maxWidth: 200,
            isMultiline: false,
            isResizable: true,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('columnGroupName', {ns: 'roles'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('columnGroupName', {ns: 'roles'})}
                    </Text>
                </>
            },
            onRender: (item: IGetRolesResponse) => {
                return <>
                    <StackItem styles={{
                        root: {
                            justifyContent: "start",
                            alignItems: "center",
                            display: "flex",
                            height: "100%"
                        }
                    }}>
                        <Text block variant={"medium"}
                              title={item.RoleName}>{item.RoleName}</Text>
                    </StackItem>
                </>
            }
        },
        {
            key: 'column2',
            name: t('columnApprovalStep', {ns: 'roles'}),
            fieldName: 'ApprovalName',
            minWidth: 100,
            isMultiline: false,
            isResizable: true,
            isCollapsible: true,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('columnApprovalStep', {ns: 'roles'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('columnApprovalStep', {ns: 'roles'})}
                    </Text>
                </>
            },
            onRender: (item: IGetRolesResponse) => {
                return <>
                    <StackItem styles={{
                        root: {
                            justifyContent: "start",
                            alignItems: "center",
                            display: "flex",
                            height: "100%"
                        }
                    }}>
                        {item.ApprovalStepId === -1 ?
                            <Text block variant={"smallPlus"}
                                  styles={{
                                      root: {
                                          padding: "4px 8px",
                                          borderRadius: 8,
                                          fontStyle: 'italic',
                                          color: branding.theme.palette.neutralTertiaryAlt,
                                          backgroundColor: branding.theme.palette.neutralLighterAlt
                                      }
                                  }}
                                  title={t('none', {ns: 'roles'})}>
                                {t('none', {ns: 'roles'})}
                            </Text>
                            :
                            <Text block variant={"medium"}
                                  title={item.ApprovalStepName}>{item.ApprovalStepName}</Text>
                        }
                    </StackItem>
                </>
            }
        },
        {
            key: 'column3',
            name: t('columnActions', {ns: 'roles'}),
            minWidth: 50,
            maxWidth: 50,
            isMultiline: false,
            isResizable: false,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('columnActions', {ns: 'roles'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('columnActions', {ns: 'roles'})}
                    </Text>
                </>
            },
            onRender: (item: IGetRolesResponse) => {
                return <>
                    <StackItem styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",
                            height: "100%"
                        }
                    }}>

                        <IconButton
                            menuIconProps={{iconName: 'MoreVertical', style: {fontSize: 20, color: '#242424'}}}
                            role="button"
                            title={t('label.colShowActions', {ns: 'common'})}
                            data={item}
                            disabled={isLoadingRoles || isDeleting}
                            aria-haspopup
                            aria-label={t('label.colShowActions', {ns: 'common'})}
                            styles={iconListButtonStyles}
                            menuProps={menuRolesProps(item)}
                        />
                    </StackItem>
                </>

            }
        }
    ]


    /**
     * Asynchronously loads roles associated with the specified template.
     *
     * This function fetches roles using the provided template ID, sorts them alphabetically by role name,
     * and updates the component state. It handles errors by displaying appropriate status messages.
     *
     * @returns {Promise<void>} A promise that resolves when the roles have been loaded and state updated.
     */

    const loadRoles = async (): Promise<void> => {

        setIsLoadingRoles(true)
        setRoles([])
        const request: IGetRolesRequest = {
            TemplateId: templateId,
            Id: templateId,
            Mode: "templateId",
        }

        await RolesService.getRoles(request)
            .then(data => {
                try {

                    if (Array.isArray(data)) {
                        data.sort((a, b) => a.RoleName.localeCompare(b.RoleName))
                        setRoles(data)
                    } else {
                        ShowMessageApprove(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } catch (error) {
                    ShowMessageApprove(t('message.error', {ns: 'common', Error: error.toString()}), StatusEnum.Error)
                }

            })
            .catch((error) => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingRoles(false)
            })
    }


    /**
     * Clears the approval message state.
     * This function resets the message, visibility, and timer status of the approval message.
     *
     * @return {void}
     */

    function ClearMessage(): void {
        setMessage('')
        setShowMessage(false)
        setHasTimerMessage(false)
    }


    /**
     * Asynchronously adds a new role.
     *
     * returns {Promise<void>} A promise that resolves when the role is added.
     */

    const addRole = async (): Promise<void> => {
        ClearMessage()
        setShowAddRole(true)
    }


    /**
     * Deletes the currently selected role.
     * This function sends a request to delete the role associated with the currentRole state.
     *
     *
     * @return {Promise<void>} A promise that resolves when the deletion is complete.
     */

    const deleteRole = async (): Promise<void> => {
        ClearMessage()
        setIsDeleting(true)

        const requestKim: IDeleteRolesRequest = {
            Id: String(currentRole!.Id ?? ''),
            ApprovalStepId: String(currentRole!.ApprovalStepId ?? ''),
            TemplateId: templateId
        }

        await RolesService.deleteRoles(requestKim)
            .then(data => {

                if (data.success) {
                    ShowMessageApprove(t('message.successRemoved', {ns: 'common'}), StatusEnum.Success, true)
                    loadRoles().then()
                } else {
                    ShowMessageApprove(t('message.error', {
                        ns: 'common',
                        Error: data.exceptionmessage
                    }), StatusEnum.Error)
                }
            })
            .catch(error => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setShowDeleteRole(false)
                setIsDeleting(false)
            })
    }


    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).newContainer}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>

                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack className="ms-hiddenXlUp">

                            <IconButton iconProps={{iconName: 'GlobalNavButton'}}
                                        style={{marginLeft: "-4px", paddingLeft: "16px"}}
                                        title={t('text.Menu', {ns: 'workspace'})} onClick={setIsOpenMenu}/>
                            <Panel
                                isBlocking={false}
                                isOpen={isOpenMenu}
                                onDismiss={setIsOpenMenu}
                                closeButtonAriaLabel={t('Common.Button.Close', {ns: 'common'})}
                                type={PanelType.smallFixedNear}
                            >
                                <Nav
                                    groups={getLinkGroup}
                                    onLinkClick={onLinkClick}
                                    selectedKey={option}
                                    styles={navStyles}
                                />
                            </Panel>

                        </Stack>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text
                                variant={"xLarge"}
                                id={titleId}
                                title={t('titleModal', {ns: 'roles'})}>
                                {t('titleModal', {ns: 'roles'})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={cancel}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack className={"ms-hiddenLgDown scrollVisible"} styles={{
                                root: {
                                    overflowY: 'auto',
                                    height: '100%',
                                    width: 240, paddingLeft: 16
                                }
                            }}>
                                <Nav
                                    groups={getLinkGroup}
                                    onLinkClick={onLinkClick}
                                    selectedKey={option}
                                    styles={navStyles}
                                />
                            </Stack>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>


                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">


                                    {showMessage &&
                                        <>
                                            <Stack styles={{root: {paddingTop: 8, paddingBottom: 8}}}>
                                                <StatusMessage status={statusMessage}
                                                               hasTimer={hasTimerMessage}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}

                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </Stack>
                                        </>
                                    }


                                    <Stack horizontal styles={{root: {alignItems: "center"}}} tokens={{childrenGap: 8}}>
                                        <StackItem grow={1}>
                                            <Text variant={'large'} block>
                                                {t('titleModal', {ns: 'roles'})}
                                            </Text>
                                        </StackItem>
                                        {roles.length >= 30 &&
                                            <StackItem>
                                                <Text variant={'medium'} styles={{root: { color: branding.theme.semanticColors.errorText}}}>
                                                    {t('roleLimitReached',{ns: 'roles'} )}
                                                </Text>
                                            </StackItem>
                                        }
                                        <StackItem>
                                            <DefaultButton
                                                title={t('addRole', {ns: 'roles'})}
                                                text={t('addRole', {ns: 'roles'})}
                                                disabled={isLoadingRoles || isDeleting || isLoadingSteps || roles.length >= 30}
                                                onClick={addRole}
                                            />
                                        </StackItem>
                                    </Stack>

                                    <Text variant={"medium"} block styles={{root: {paddingTop: 4}}}>
                                        {t('deleteText', {ns: 'roles'})}
                                    </Text>



                                    <ShimmeredDetailsList
                                        items={roles}
                                        enableShimmer={isLoadingRoles}
                                        compact={false}
                                        columns={rolesColumns}
                                        selectionMode={SelectionMode.none}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        isHeaderVisible={true}
                                        isSelectedOnFocus={false}
                                        setKey={"Id"}
                                    />

                                    {roles.length === 0 && !isLoadingRoles &&
                                        <>
                                            <p style={{textAlign: "center"}}>
                                                <Text variant={'small'}>
                                                    <Trans>
                                                        {t('empty', {ns: 'roles'})}
                                                    </Trans>
                                                </Text>
                                            </p>
                                        </>
                                    }


                                </Stack>


                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    {(isLoadingRoles || isLoadingSteps) &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                         label={t('text.spinner', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    {isDeleting &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                         label={t('text.spinnerDeleting', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    <Stack horizontal tokens={{childrenGap: 8}} wrap={false}>


                                        <DefaultButton onClick={cancel}
                                                       text={t('Common.Button.Close', {ns: 'common'})}
                                                       title={t('Common.Button.Close', {ns: 'common'})}/>

                                    </Stack>
                                </Stack>


                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Modal>

            <CustomDialog title={t('Common.Button.Delete', {ns: 'common'})} titleId={`remove${templateId}`}
                          actionText={t('Common.Button.Delete', {ns: 'common'})}
                          isDisabled={isDeleting}
                          confirm={deleteRole}
                          close={() => {
                              setShowDeleteRole(false)
                          }}
                          isOpen={showDeleteRole}
            >
                <Trans>
                    {t('messageRoleConfirmDeleting', {ns: 'roles'})}
                </Trans>
            </CustomDialog>

            {showAddRole &&
                <RolesAdd
                    showModal={showAddRole}
                    onDismissModal={onCloseAddModal}
                    stepsCBOptions={stepsCBOptions}
                    templateId={templateId}/>
            }

        </>
    )
}
